// Copyright 2025, University of Maryland, All Rights Reserved
import { Fragment, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'

/** Stable default for isRowSelected to avoid creating new function each render */
const alwaysFalse = () => false

export interface DashboardTableColumn<TRow> {
  key: string
  header: ReactNode
  /** Label shown in mobile card view (e.g. "Doc ID"). Omit to hide column on mobile. */
  mobileLabel?: string
  className?: string
  render: (row: TRow) => ReactNode
}

type SelectionMode = 'multiple' | 'none'

interface DashboardTableProps<TRow> {
  rows: TRow[]
  columns: DashboardTableColumn<TRow>[]
  resolveRowId: (row: TRow) => string
  isRowSelected?: (rowId: string) => boolean
  onToggleRow?: (rowId: string) => void
  emptyLabel: string
  loading?: boolean
  previewRowId?: string | null
  renderPreview?: (row: TRow) => ReactNode
  selectionMode?: SelectionMode
  /** Custom mobile card renderer. When provided, replaces the default label:value card layout. */
  renderMobileCard?: (row: TRow) => ReactNode
}

export const DashboardTable = <TRow,>({
  rows,
  columns,
  resolveRowId,
  isRowSelected,
  onToggleRow,
  emptyLabel,
  loading = false,
  previewRowId = null,
  renderPreview,
  selectionMode = 'multiple',
  renderMobileCard,
}: DashboardTableProps<TRow>) => {
  const selectionEnabled = selectionMode !== 'none' && typeof onToggleRow === 'function'
  const hasCustomMobileCard = typeof renderMobileCard === 'function'
  const isActiveRowSelected = isRowSelected ?? alwaysFalse

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span className="text-sm">Loading documents…</span>
      </div>
    )
  }

  if (rows.length === 0) {
    return <div data-testid="cr-empty-state" className="py-10 text-center text-sm text-slate-600">{emptyLabel}</div>
  }

  return (
    <div className="space-y-3">
      {/* Desktop: table layout */}
      <div className="hidden md:block">
        <Table className="w-full" data-testid="cr-doc-table">
          <TableHeader className="bg-muted/60 text-xs uppercase">
            <TableRow>
              {selectionEnabled ? <TableHead className="w-10" /> : null}
              {columns.map(column => (
                <TableHead key={column.key} className={column.className}>
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => {
              const rowId = resolveRowId(row)
              const isSelected = selectionEnabled ? isActiveRowSelected(rowId) : false
              const isPreviewRow = previewRowId !== null && previewRowId === rowId

              return (
                <Fragment key={rowId}>
                  <TableRow data-preview={isPreviewRow ? 'true' : undefined} data-testid={`cr-doc-row-${rowId}`}>
                    {selectionEnabled ? (
                      <TableCell className="w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-muted"
                          checked={isSelected}
                          onChange={() => onToggleRow?.(rowId)}
                        />
                      </TableCell>
                    ) : null}
                    {columns.map(column => (
                      <TableCell key={column.key} className={column.className}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isPreviewRow && renderPreview ? (
                    <TableRow className="bg-muted/40">
                      <TableCell colSpan={columns.length + 1} className="p-0">
                        {renderPreview(row)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: stacked card layout */}
      <div className="flex flex-col gap-3 md:hidden">
        {rows.map(row => {
          const rowId = resolveRowId(row)

          if (hasCustomMobileCard) {
            return <Fragment key={rowId}>{renderMobileCard(row)}</Fragment>
          }

          const isSelected = selectionEnabled ? isActiveRowSelected(rowId) : false
          return (
            <div key={rowId} className="rounded-lg border border-border bg-card p-4 space-y-3">
              {selectionEnabled && (
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-muted"
                  checked={isSelected}
                  onChange={() => onToggleRow?.(rowId)}
                />
              )}
              {columns.map(column => {
                if (!column.mobileLabel) return null
                return (
                  <div key={column.key}>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      {column.mobileLabel}
                    </div>
                    <div>{column.render(row)}</div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
